import { postPaymentStatus, postPOSPayment } from "@/api/payment";
import { ACCErrors } from "@/constants/errors/accounting";
import { PaymentErrors } from "@/constants/errors/payment";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { AdyenResponseStatus } from "@/interfaces/store/IBooking";
import BookingModule from "@/store/modules/booking";
import DeviceModule from "@/store/modules/device";
import POSConfigModule from "@/store/modules/pos-config";
import {
  AccountingState,
  IReceiptPaymentDetails,
  PaymentMethods,
  TransactionTypes,
} from "@/types/db/accounting";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import {
  getGroupAccountCodeAndType,
  getGroupInfo,
} from "@/utils/helpers/group";
import { checkTerminalConnectivity } from "@/utils/vue-helpers/common";
import { getMostRecentAdyenAccounting } from "@/utils/vue-helpers/new-accountings";

import { MessageCategoryType } from "@adyen/api-library/lib/src/typings/terminal/messageCategoryType";
import { MessageClassType } from "@adyen/api-library/lib/src/typings/terminal/messageClassType";
import { MessageReference } from "@adyen/api-library/lib/src/typings/terminal/messageReference";
import { MessageType } from "@adyen/api-library/lib/src/typings/terminal/messageType";
import { Response } from "@adyen/api-library/lib/src/typings/terminal/response";
import { ResultType } from "@adyen/api-library/lib/src/typings/terminal/resultType";
import { SaleToPOIResponse } from "@adyen/api-library/lib/src/typings/terminal/saleToPOIResponse";
import { TerminalApiRequest } from "@adyen/api-library/lib/src/typings/terminal/terminalApiRequest";
import { TerminalApiResponse } from "@adyen/api-library/lib/src/typings/terminal/terminalApiResponse";
import { TransactionStatusRequest } from "@adyen/api-library/lib/src/typings/terminal/transactionStatusRequest";
import dayjs from "dayjs";
import { last, pick, sumBy } from "lodash";
import qs from "qs-lite";
import randomstring from "randomstring";
import { LogLevel, LogService } from "./logging";

export enum PaymentServiceStatus {
  Waiting = "Waiting", // Initial state. Waiting to start a payment process
  InProgress = "InProgress",
  CantCheckStatus = "CantCheckStatus", // Can't check status
  Failed = "Failed",
  Canceled = "Cancelled", // Cancelled from terminal
  Aborted = "Aborted", // Cancelled from cash register
  Success = "Success", // Payment successful
  UnKnownError = "UnKnownError",
  NotFound = "NotFound",
  Busy = "Busy",
  Cancel = "Cancel",
  DeviceOut = "DeviceOut",
  LowBattery = "LowBattery",
  InvalidCard = "InvalidCard",
  NotAllowed = "NotAllowed",
  Refusal = "Refusal",
  UnreachableHost = "UnreachableHost",
  WrongPIN = "WrongPIN",
}

// const config = new Config();
class AdyenPaymentService {
  public poiId = "";
  public saleId = "";

  public serviceId = "";
  public message = "";
  public transactionId = "";
  public status: PaymentServiceStatus = PaymentServiceStatus.Waiting;

  public onUpdate: ((status: PaymentServiceStatus) => void) | null = null;
  public abortControllers: AbortController[] = []; // callbacks to abort payment requests

  // Payment details
  public pspReference = "";
  public posPaymentMethod = "";
  public fullTransactionId = "";
  public paymentDetails: IReceiptPaymentDetails | null = null;

  public manuallyCompleted = false;

  public retriesAfterStatusCheckFailure = 0;

  public createPaymentInfo() {
    // Cash Register unique id
    // const saleId = DeviceModule.adyenSaleId;
    const saleId = this.saleId;
    /**
     * Unique id for this request. Must be unique for the last 48hours
     * for the terminal being used.
     */
    // const serviceId = uuid();
    const serviceId = randomstring.generate(10);
    /**
     * Unique id for the terminal
     */
    // const poiId = POSConfigModule.getPosTerminal;
    const poiId = this.poiId;

    if (!poiId || !saleId) {
      throw new Error(
        `${PaymentErrors.RequiredDeviceInfoMissing} accounting: ${this.transactionId}`
      );
    }

    return {
      saleId,
      serviceId,
      poiId,
    };
  }

  /**
   * create Accounting, Accounting entries, and we update PSS payment data
   */
  public async createPOSPayment(booking: IProductSlotBooking) {
    const accounting = getMostRecentAdyenAccounting(booking.accountings);
    if (!accounting) {
      // throw new CustomError(PSBErrors.HasNoAccounting, {options: booking});
      throw new Error(PSBErrors.HasNoAccounting + JSON.stringify(booking));
    }
    if (!accounting.uuid) {
      throw new Error(ACCErrors.HasNoUUID + JSON.stringify(booking));
    }

    let amount = 0;
    // Consider only Adyen accountings that are not paid yet and haven't failed before, to calculate the amount to request from Adyen
    const paymentAccountings = booking.accountings.filter((acc) => {
      return (
        !acc.id &&
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT &&
        acc.paymentMethod === PaymentMethods.METHOD_POS_ADYEN &&
        acc.state !== AccountingState.Completed &&
        acc.state !== AccountingState.Failed
      );
    });

    for (const acc of paymentAccountings) {
      const accountingEntries = acc.accountingEntriesList;
      if (!accountingEntries || !accountingEntries.length) {
        throw new Error(
          PSBErrors.HasNoAccountingEntries + JSON.stringify(booking)
        );
      }

      // Adyen doesn't take amounts in cents
      amount += Math.round(sumBy(accountingEntries, (ae) => ae.value)) / 100;
    }
    // build the payload
    const currency = booking.currency;
    // Use NEW_POS_groupid_bookingUuid_accountingUuid
    const groupId = String(POSConfigModule.groupId);
    const bookingUuid = booking.uuid;
    this.transactionId =
      "NEW_POS_" + groupId + "_" + bookingUuid + "_" + accounting.uuid;

    const { saleId, poiId } = this.createPaymentInfo();
    // Backend requests that transactionId is same as service id
    // this is ok, since one accounting is created for a payment, no more.
    const serviceId = accounting.uuid;
    // Set service id so that we can check the status of the payment
    this.serviceId = serviceId;
    const timestamp = dayjs().toISOString();

    const params: TerminalApiRequest = {
      SaleToPOIRequest: {
        MessageHeader: {
          ProtocolVersion: "3.0",
          MessageClass: MessageClassType.Service,
          MessageCategory: MessageCategoryType.Payment,
          MessageType: MessageType.Request,
          SaleID: saleId,
          ServiceID: serviceId,
          POIID: poiId,
        },
        PaymentRequest: {
          SaleData: {
            SaleTransactionID: {
              TransactionID: this.transactionId,
              TimeStamp: timestamp,
            },
          },
          PaymentTransaction: {
            AmountsReq: {
              Currency: currency,
              RequestedAmount: amount,
            },
          },
        },
      },
    };

    // Use account code for split payments
    const { accountCode, accountType } = getGroupAccountCodeAndType();
    if (!accountCode) {
      const groupInfo = getGroupInfo();
      LogService.info(
        `Error! group has no account code, booking: ${booking.uuid}, accounting: ${accounting.uuid}`,
        groupInfo,
        LogLevel.Error
      );
      throw new Error(`Group has no account code, ${groupInfo}`);
    }
    const splitReference = this.transactionId.replace(
      "NEW_POS_",
      "NEW_POS_SPLIT_"
    );

    // Update accounting with payment data
    accounting.transactionTimestamp = timestamp;
    accounting.saleId = saleId;
    accounting.poiId = poiId;
    accounting.transactionId = this.transactionId;
    accounting.splitReference = splitReference;
    accounting.accountCode = accountCode;

    // Split must be provided in minor units and as an integar
    const splitAmount = Math.round(amount * 100);
    const groupName = POSConfigModule.groupInfo?.name;
    (
      params.SaleToPOIRequest.PaymentRequest as any
    ).SaleData.SaleToAcquirerData = qs.stringify({
      shopperStatement: String(groupName),
      "split.api": "1",
      "split.nrOfItems": "1",
      "split.totalAmount": splitAmount, // in miner units
      "split.currencyCode": currency,
      "split.item1.amount": splitAmount, // in miner units
      "split.item1.type": accountType,
      "split.item1.account": accountCode,
      "split.item1.reference": splitReference,
    });
    // Leave the following as Riyadh will modify the Adyen SDK to support this.
    // ({
    //   split: {
    //     "api": "1",
    //     "nrOfItems": "1",
    //     "totalAmount": amount * 100, // in miner units
    //     "currencyCode": currency,
    //     item1: {
    //       "amount": amount * 100, // in miner units
    //       "type": "MarketPlace",
    //       "account": "8816442467767622",
    //       "reference": "NEW_POS_" + this.transactionId // random refrence
    //     }
    //   }
    // });

    LogService.info(
      `(compressed) Initiaing Adyen payment, booking: ${booking.uuid}, accounting: ${accounting.uuid}`,
      { selectedBooking: booking, params }
    );
    // store the initiated adyen payment
    BookingModule.setAdyenResponseStatusTrack({
      bookingUuid: booking.uuid!,
      accountingUuid: accounting.uuid,
      status: AdyenResponseStatus.Initiated,
    });
    let response: TerminalApiResponse | null = null;
    try {
      console.log("going to check terminal connection");
      const promise = checkTerminalConnectivity(
        params.SaleToPOIRequest.MessageHeader.POIID
      );

      response = await postPOSPayment(params);
      LogService.info(
        `Adyen payment response received, booking: ${booking.uuid}, accounting: ${accounting.uuid}`,
        response
      );
      BookingModule.setAdyenResponseStatusTrack({
        bookingUuid: booking.uuid!,
        accountingUuid: accounting.uuid,
        status: AdyenResponseStatus.Completed,
      });
    } catch (error) {
      LogService.info(
        `Error received from Adyen payment request, booking: ${booking.uuid}, accounting: ${accounting.uuid}`,
        error,
        LogLevel.Error
      );
      BookingModule.setAdyenResponseStatusTrack({
        bookingUuid: booking.uuid!,
        accountingUuid: accounting.uuid,
        status: AdyenResponseStatus.InitialPaymentFailed,
      });
      // if the payment was cancelled by the controller do not attempt a status check
      if (error.message === "canceled") {
        return;
      }
      return this.paymentStatus();
    }

    if (response) {
      return this.handleAdyenResponse(response);
    }

    LogService.info(
      `No response received from Adyen payment, booking: ${booking.uuid}, accounting: ${accounting.uuid}`,
      undefined,
      LogLevel.Error
    );
    BookingModule.setAdyenResponseStatusTrack({
      bookingUuid: booking.uuid!,
      accountingUuid: accounting.uuid,
      status: AdyenResponseStatus.Unresponsive,
    });

    // this.paymentStatus();

    // try {
    // Call payment on server
    // TODO @Riyadh, run payment on device once we setup React Native / Node

    // if (location.search.includes("test-adyen=pos")) {
    //   response = salesResponse as any;
    // }

    // const { SaleToPOIRequest, SaleToPOIResponse } = response;

    // // No response, failed case
    // if (!SaleToPOIResponse && SaleToPOIRequest) {
    //   const notification = SaleToPOIRequest.EventNotification;
    //   accounting.state = AccountingState.Failed;
    //   accounting.message = notification
    //     ? JSON.stringify(pick(notification, ["EventDetails", "TimeStamp"]))
    //     : "Transaction was failed.";
    //   throw new Error(JSON.stringify(SaleToPOIRequest));
    // } else if (SaleToPOIResponse) {
    //   // const result = SaleToPOIResponse.PaymentResponse?.Response.Result;
    //   // const pResponse = SaleToPOIResponse.PaymentResponse;
    //   // if (pResponse) {
    //   //   this.handlePaymentResponse(pResponse, booking);
    //   // }
    //   // if (result !== ResultType.Success) {

    //   // }
    //   // Success case
    //   const paymentAcquirerData =
    //     SaleToPOIResponse.PaymentResponse?.PaymentResult?.PaymentAcquirerData;
    //   const additionalInfo =
    //     SaleToPOIResponse.PaymentResponse?.Response.AdditionalResponse;
    //   let pspReference = "";
    //   // set accounting.pspReference
    //   if (paymentAcquirerData) {
    //     pspReference =
    //       paymentAcquirerData.AcquirerTransactionID?.TransactionID || "";
    //   }

    //   if (!pspReference) {
    //     // accounting.state = AccountingState.Failed;
    //     // accounting.message = "Transaction was failed.";
    //     throw new Error("No Psp reference " + JSON.stringify(response));
    //   }

    //   accounting.state = AccountingState.Completed;
    //   accounting.message =
    //     "Transaction was completed." +
    //     JSON.stringify(paymentAcquirerData || "");

    //   // set accounting.posPaymentMehod
    //   if (additionalInfo) {
    //     accounting.posPaymentMethod =
    //       qs.parse(additionalInfo).paymentMethod || "";
    //   }

    //   // update all pss
    //   for (const pss of booking.pssList) {
    //     pss.bookingStatus = BookingStatus.Confirmed;
    //     pss.paymentStatus = PaymentStatus.Completed;
    //   }
    //   booking.state = BookingState.Completed;
    //   booking.message = BookingStateString[BookingState.Completed];
    // }
    // } catch (err) {
    //   // Payment error Error: timeout of 20000ms exceeded
    //   // Set the error in case we are not dealing with an adyen failure.
    //   // could be request failure or something else
    //   if (accounting.state !== AccountingState.Failed) {
    //     accounting.state = AccountingState.Failed;
    //     accounting.message = err.message;
    //   }
    //   throw err;
    // }
  }

  public setStatus(status: PaymentServiceStatus) {
    const bookingUuid = BookingModule.selectedBookingUUID;
    LogService.info(
      `Setting payment status to status: ${status}, booking: ${bookingUuid}`
    );
    this.status = status;
    if (this.onUpdate) {
      this.onUpdate(status);
    }
  }

  public async paymentStatus() {
    if (!this.serviceId) {
      LogService.info("No service id set, skipping payment status check");
      return;
    }
    // Why are we resetting poiId and saleId below?
    // Because in the case there is no response from Adyen for the payment request, user will try to abort payment
    // which will clear payment service, and in such scenario createPaymentInfo() will throw a useless error
    // so we're setting below values to keep paymentStatus request correct
    this.poiId = POSConfigModule.getPosTerminal;
    this.saleId = DeviceModule.adyenSaleId;

    const { saleId, poiId, serviceId } = this.createPaymentInfo();
    const params: TerminalApiRequest = {
      SaleToPOIRequest: {
        MessageHeader: {
          ProtocolVersion: "3.0",
          MessageClass: MessageClassType.Service,
          MessageCategory: MessageCategoryType.TransactionStatus,
          MessageType: MessageType.Request,
          SaleID: saleId,
          // ServiceID: serviceId,
          ServiceID: serviceId,
          POIID: poiId,
        },
        TransactionStatusRequest: {
          ReceiptReprintFlag: true,
          DocumentQualifier: [
            TransactionStatusRequest.DocumentQualifierEnum.CashierReceipt,
            TransactionStatusRequest.DocumentQualifierEnum.CustomerReceipt,
          ],
          MessageReference: {
            SaleID: saleId,
            ServiceID: this.serviceId, // last transaction
            MessageCategory: MessageReference.MessageCategoryEnum.Payment,
          },
        },
      },
    };

    LogService.info(`Initiating Adyen status request`, { params });

    try {
      const response = await postPaymentStatus(params);
      LogService.info(`Adyen status response received (attached)`, {
        response,
      });
      return this.handleAdyenResponse(response);
    } catch (error) {
      this.message = `${PaymentServiceStatus.CantCheckStatus} ${
        error && (error as any).message
      }`;
      LogService.info(
        `Error when requesting Adyen status`,
        { error },
        LogLevel.Error
      );
      this.setStatus(PaymentServiceStatus.CantCheckStatus);
    }

    // console.log(response.SaleToPOIResponse?.TransactionStatusResponse?.Response.Result);
  }

  public handleAdyenResponse(response: TerminalApiResponse) {
    // skip if the user already completed the accounting manually
    if (this.manuallyCompleted) return;
    if (!this.serviceId) {
      LogService.info("Service id already cleared, skipping response handling");
      return;
    }

    LogService.info(`Handling Adyen response`, response);
    const { SaleToPOIRequest, SaleToPOIResponse } = response;

    // No response, failed case
    if (!SaleToPOIResponse && SaleToPOIRequest) {
      const notification = SaleToPOIRequest.EventNotification;
      // accounting.state = AccountingState.Failed;
      // accounting.message = notification

      this.message = notification
        ? JSON.stringify(pick(notification, ["EventDetails", "TimeStamp"]))
        : "Transaction was failed.";
      this.setStatus(PaymentServiceStatus.Failed);
      LogService.info(
        `No response is received from Adyen`,
        undefined,
        LogLevel.Error
      );
      return;
    }

    const transactionStatus = SaleToPOIResponse?.TransactionStatusResponse;

    // Case of a transaction status
    if (transactionStatus) {
      LogService.info(`Handling transaction status response`);
      const transactionResponse = transactionStatus.Response;
      const transcactionResult = transactionResponse.Result;
      // Handle failed ops
      if (transcactionResult === ResultType.Failure) {
        // In progress
        if (
          transactionResponse.ErrorCondition ===
          Response.ErrorConditionEnum.InProgress
        ) {
          this.setStatus(PaymentServiceStatus.InProgress);
          // Retry after 3 seconds;
          return;
        }

        if (
          transactionResponse.ErrorCondition ===
          Response.ErrorConditionEnum.NotFound
        ) {
          this.message = PaymentServiceStatus.NotFound;
          this.setStatus(PaymentServiceStatus.NotFound);
          return;
        }

        // Unknown Error
        this.message =
          PaymentErrors.UnknownFailureTransactionCondition +
          ` ${transactionResponse.ErrorCondition}`;
        LogService.info(
          `Unknown transaction status response, ${transactionResponse.ErrorCondition}`,
          undefined,
          LogLevel.Error
        );
        this.setStatus(PaymentServiceStatus.UnKnownError);
        LogService.info(this.message, response);
        return;
      }

      if (transcactionResult === ResultType.Success) {
        const repeatedMessageBody =
          transactionStatus.RepeatedMessageResponse
            ?.RepeatedResponseMessageBody;
        if (!repeatedMessageBody) {
          this.message = PaymentErrors.NoRepeatedMessageBody;
          this.setStatus(PaymentServiceStatus.UnKnownError);
          LogService.info(PaymentErrors.NoRepeatedMessageBody, response);
          return;
        }

        const paymentResponse = repeatedMessageBody.PaymentResponse;
        if (!paymentResponse) {
          this.message = PaymentErrors.NoPaymentResponseInTransaction;
          this.setStatus(PaymentServiceStatus.UnKnownError);
          LogService.info(
            PaymentErrors.NoPaymentResponseInTransaction,
            response
          );
          return;
        }

        return this.handlePaymentResponse(paymentResponse);
      }

      this.message = `${PaymentErrors.UnknownTransactionResult} ${transcactionResult}`;
      this.setStatus(PaymentServiceStatus.UnKnownError);
      LogService.info(`Transaction status error: ${this.message}`);
      return;
    }

    const paymentResponse = SaleToPOIResponse?.PaymentResponse;
    // Case of a create pos payment
    if (paymentResponse) {
      this.handlePaymentResponse(paymentResponse);
      return;
    }

    this.message = PaymentErrors.UnknownAdyenResponse;
    this.setStatus(PaymentServiceStatus.UnKnownError);
    LogService.info(this.message, response);
  }

  public handlePaymentResponse(
    // paymentResponse: /*PaymentResponse*/RepeatedResponseMessageBody['PaymentResponse'], booking: IProductSlotBooking) {
    // paymentResponse: SaleToPOIResponse['PaymentResponse'], booking: IProductSlotBooking) {
    paymentResponse: Exclude<SaleToPOIResponse["PaymentResponse"], undefined>
  ) {
    LogService.info(`Handling payment response`, paymentResponse);
    const {
      Result: paymentResult,
      ErrorCondition: errorCondition,
      AdditionalResponse: additionalResponse,
    } = paymentResponse.Response;

    // Failed payments
    if (paymentResult === ResultType.Failure) {
      switch (errorCondition) {
        case Response.ErrorConditionEnum.Aborted:
          this.message = PaymentServiceStatus.Aborted;
          this.setStatus(PaymentServiceStatus.Aborted);
          break;
        case Response.ErrorConditionEnum.Cancel:
          this.message = PaymentServiceStatus.Canceled;
          this.setStatus(PaymentServiceStatus.Canceled);
          break;
        case Response.ErrorConditionEnum.Busy:
          this.message = PaymentServiceStatus.Busy;
          this.setStatus(PaymentServiceStatus.Busy);
          break;
        case Response.ErrorConditionEnum.DeviceOut: {
          const parsedResponse = new URLSearchParams(additionalResponse);
          const status = parsedResponse.get("message")?.includes("Low Battery")
            ? PaymentServiceStatus.LowBattery
            : PaymentServiceStatus.DeviceOut;
          this.message = status;
          this.setStatus(status);
          break;
        }
        case Response.ErrorConditionEnum.InvalidCard:
          this.message = PaymentServiceStatus.InvalidCard;
          this.setStatus(PaymentServiceStatus.InvalidCard);
          break;
        case Response.ErrorConditionEnum.NotAllowed:
          this.message = PaymentServiceStatus.NotAllowed;
          this.setStatus(PaymentServiceStatus.NotAllowed);
          break;
        case Response.ErrorConditionEnum.Refusal:
          this.message = PaymentServiceStatus.Refusal;
          this.setStatus(PaymentServiceStatus.Refusal);
          break;
        case Response.ErrorConditionEnum.UnreachableHost:
          this.message = PaymentServiceStatus.UnreachableHost;
          this.setStatus(PaymentServiceStatus.UnreachableHost);
          break;
        case Response.ErrorConditionEnum.WrongPin:
          this.message = PaymentServiceStatus.WrongPIN;
          this.setStatus(PaymentServiceStatus.WrongPIN);
          break;
        default:
          this.message = PaymentErrors.UnknownPaymentFailure;
          this.setStatus(PaymentServiceStatus.UnKnownError);
          break;
      }
      return;
    }

    // Successfule payment
    if (paymentResult === ResultType.Success) {
      const paymentAcquirerData =
        paymentResponse?.PaymentResult?.PaymentAcquirerData;
      const additionalInfo = paymentResponse?.Response.AdditionalResponse;
      // let pspReference = "";
      // set accounting.pspReference
      if (paymentAcquirerData) {
        this.pspReference =
          paymentAcquirerData.AcquirerTransactionID?.TransactionID || "";
      }

      // Retrieve full transaction id
      const poiData = paymentResponse.POIData;
      if (poiData) {
        this.fullTransactionId = poiData.POITransactionID.TransactionID;
      }
      // if (!pspReference) {
      // accounting.state = AccountingState.Failed;
      // accounting.message = "Transaction was failed.";
      // throw new Error("No Psp reference " + JSON.stringify());
      // }

      this.status = PaymentServiceStatus.Success;
      // accounting.state = AccountingState.Completed;
      this.message =
        // accounting.message =
        "Transaction was completed." +
        JSON.stringify(paymentAcquirerData || "");

      // set accounting.posPaymentMehod
      if (additionalInfo) {
        // We put parsing in a Try/Catch block because it may fail, and we don't want to keep the user stuck with the payment popup
        let parsedAdditionalInfo = null;
        try {
          // trying to parse the additionalInfo string from Adyen response using a qs package
          parsedAdditionalInfo = qs.parse(additionalInfo);
          this.posPaymentMethod = parsedAdditionalInfo.paymentMethod || "";
          this.paymentDetails = {
            date: parsedAdditionalInfo.txdate || "",
            time: parsedAdditionalInfo.txtime || "",
            card: parsedAdditionalInfo.cardSummary || "",
            status: parsedAdditionalInfo.acquirerResponseCode || "",
            authCode: paymentAcquirerData?.ApprovalCode || "",
            tender: this.fullTransactionId.split(".")[0] || "",
            reference: parsedAdditionalInfo.transactionReferenceNumber || "",
            AID: parsedAdditionalInfo.AID || "",
            MID: parsedAdditionalInfo.mid || "",
            TID: paymentAcquirerData?.AcquirerPOIID || "",
            PTID: parsedAdditionalInfo.tid || "",
            currency: parsedAdditionalInfo.posAuthAmountCurrency || "",
            total:
              (Number(parsedAdditionalInfo.posAuthAmountValue) / 100).toFixed(
                2
              ) || "",
            posEntryMode: parsedAdditionalInfo.posEntryMode || "",
            paymentMethod: this.posPaymentMethod,
          };
        } catch (err) {
          LogService.info(
            `error when trying to parse additionalInfo using QS package`,
            { params: err },
            LogLevel.Error
          );
          // Lets try the vanilla way
          try {
            const parsedAdditionalInfo = new URLSearchParams(additionalInfo);
            this.posPaymentMethod =
              parsedAdditionalInfo.get("paymentMethod") || "";
            this.paymentDetails = {
              date: parsedAdditionalInfo.get("txdate") || "",
              time: parsedAdditionalInfo.get("txtime") || "",
              card: parsedAdditionalInfo.get("cardSummary") || "",
              status: parsedAdditionalInfo.get("acquirerResponseCode") || "",
              authCode: paymentAcquirerData?.ApprovalCode || "",
              tender: this.fullTransactionId.split(".")[0] || "",
              reference:
                parsedAdditionalInfo.get("transactionReferenceNumber") || "",
              AID: parsedAdditionalInfo.get("AID") || "",
              MID: parsedAdditionalInfo.get("mid") || "",
              TID: paymentAcquirerData?.AcquirerPOIID || "",
              PTID: parsedAdditionalInfo.get("tid") || "",
              currency: parsedAdditionalInfo.get("posAuthAmountCurrency") || "",
              total:
                (
                  Number(parsedAdditionalInfo.get("posAuthAmountValue")) / 100
                ).toFixed(2) || "",
              posEntryMode: parsedAdditionalInfo.get("posEntryMode") || "",
              paymentMethod: this.posPaymentMethod,
            };
          } catch (err2) {
            LogService.info(
              `Error when trying to parse additionalInfo using vanilla way`,
              { params: err2 },
              LogLevel.Error
            );
          }
        }
      }

      const bookingUuid = BookingModule.selectedBookingUUID;
      /**
        we make sure to set the response status of the current service ID to completed to avoid reporting the payment as failed later when closing the booking
        in case this successful response comes from checking paymentStatus and not from the initial payment api call
       */
      BookingModule.setAdyenResponseStatusTrack({
        bookingUuid: bookingUuid,
        accountingUuid: this.serviceId,
        status: AdyenResponseStatus.Completed,
      });
      this.setStatus(PaymentServiceStatus.Success);
      return;
    }

    // We should never get here
    this.setStatus(PaymentServiceStatus.Failed);
    LogService.info(PaymentErrors.UnknownPaymentResult, { paymentResponse });
  }

  public resetPaymentService() {
    LogService.info(`Cleaning payment service`);

    if (this.abortControllers.length) {
      this.abortControllers.forEach((controller) => controller.abort());
      this.abortControllers = [];
    }

    this.poiId = "";
    this.saleId = "";

    this.serviceId = "";
    this.message = "";
    this.transactionId = "";
    this.status = PaymentServiceStatus.Waiting;
    // Payment details
    this.pspReference = "";
    this.posPaymentMethod = "";
    this.fullTransactionId = "";
    this.paymentDetails = null;

    this.retriesAfterStatusCheckFailure = 0;
  }

  /**
   * Aborts Adyen payments
   * @param booking booking for which we will abort the payment
   * @param once if set to true, no payment status check will be performed in case the abort request fails
   */
  public async abortPayment(booking: IProductSlotBooking) {
    // const accounting = last(booking.accountings);
    // We cancel the current payment
    const accountingUuidToAbort = this.serviceId;
    if (!accountingUuidToAbort) {
      LogService.info(
        `${ACCErrors.HasNoUUID} when trying to abort payment ${PaymentService.transactionId}`,
        undefined,
        LogLevel.Error
      );
      throw new Error(ACCErrors.HasNoUUID);
    }
    const { saleId, poiId } = this.createPaymentInfo();
    const serviceId = accountingUuidToAbort;
    this.serviceId = serviceId;
    const cancelParams: TerminalApiRequest = {
      SaleToPOIRequest: {
        MessageHeader: {
          ProtocolVersion: "3.0",
          MessageClass: MessageClassType.Service,
          MessageCategory: MessageCategoryType.Abort,
          MessageType: MessageType.Request,
          SaleID: saleId,
          ServiceID: randomstring.generate(10), //serviceID of the abort request header needs to be unique
          POIID: poiId,
        },
        AbortRequest: {
          AbortReason: "MerchantAbort",
          MessageReference: {
            MessageCategory: MessageReference.MessageCategoryEnum.Payment,
            SaleID: saleId,
            ServiceID: serviceId,
          },
        },
      },
    };
    let response: TerminalApiResponse | null = null;
    try {
      LogService.info(
        `Cancelling Adyen payment, booking: ${booking.uuid}, accounting: ${accountingUuidToAbort}`,
        cancelParams
      );
      response = await postPOSPayment(cancelParams);
      if (response) {
        LogService.info(
          `Adyen abort request sent, waiting for a response from adyen, booking: ${booking.uuid}, accounting: ${accountingUuidToAbort}`
        );
      }
    } catch (error) {
      // do not attempt a status check if the abort request was cancelled by the controller
      if (error.message === "canceled") {
        LogService.info(
          `Adyen abort request was cancelled by the caller, booking: ${booking.uuid}, accounting: ${accountingUuidToAbort}`
        );
        return;
      }
      LogService.info(
        "Payment abort request failed (error attached), going to check payment status",
        { error }
      );
      PaymentService.paymentStatus();
    }
  }

  /**
   * Complete the payment automatically:
   * - set the status to Success
   * - set manuallyCompleted true true
   * @param booking
   */
  public completePaymentManually(booking: IProductSlotBooking) {
    const accountingUuid = this.serviceId;
    LogService.info(
      `Going to manually complete Adyen payment, booking: ${booking.uuid}, accounting: ${accountingUuid}`
    );
    this.manuallyCompleted = true;
    this.setStatus(PaymentServiceStatus.Success);
    LogService.info(
      `Done completing Adyen payment manually, booking: ${booking.uuid}, accounting: ${accountingUuid}`
    );
    return;
  }
}

const PaymentService = new AdyenPaymentService();

export default PaymentService;
